import { Dispatch, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import type { Action } from 'redux';

import lang from '../../../lib/language';
import { PeopleAlsoTradeItem, ReportingCall } from '../../../lib/models/reporting/types';
import { getCurrentAccount } from '../../../lib/store/crm/helpers';
import { RootState } from '../../../lib/store/index';
import { getPeopleAlsoTradeData } from '../../../lib/store/reporting';
import { isCallStatusPending, isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';

import { useIsMobile } from '../../../hooks';
import { useSubscriptionManager } from '../../../hooks/useSubscriptionManager';
import { NoDataAvailable, Preloader } from '../../index';

import CalculatedTradePriceCache from './components/PeopleAlsoTradeCard/CalculatedTradePriceCache';
import PeopleAlsoTradeCard from './components/PeopleAlsoTradeCard/PeopleAlsoTradeCard';
import SlideButtons from './components/SlideButtons/SlideButtons';
import { TradePrice } from './PeopleAlsoTrade.types';

import styles from './PeopleAlsoTrade.module.scss';

type OwnProps = { symbol: string }
type Props = OwnProps & ReduxProps;

let sortedData: TradePrice[] = [];

const PeopleAlsoTrade = (props: Props) => {
  const {
    symbol,
    peopleAlsoTradeData,
    accountReferenceId,
    isPeopleAlsoTradeDataPending,
    isPeopleAlsoTradeDataReady,
    getDataPeopleAlsoTrade,
  } = props;
  const { isMobile } = useIsMobile();
  const [slideIndex, setSlideIndex] = useState<number>(0);
  const [tempData, setTempData] = useState<TradePrice[]>([]);
  const [dataCards, setDataCards] = useState<PeopleAlsoTradeItem[]>([]);

  const handleTradePriceCache = (data: TradePrice) => {
    if (tempData.every(item => item.symbol !== data.symbol)) {
      setTempData([ ...tempData, data ]);
    }
  };

  const renderPeopleAlsoTradeCards = () => {
    const handleClickCard = () => {
      setSlideIndex(0);
    };

    const peopleAlsoTradeCards = (priceCacheData: TradePrice[]) => (
      priceCacheData.map((item: TradePrice) => (
        <PeopleAlsoTradeCard
          data={item}
          key={item.symbol}
          handleClickCard={handleClickCard}
        />
      ))
    );

    sortedData = tempData.filter(item => item.symbol !== symbol).slice().sort((a, b) =>
      parseFloat(b.changePercent || '') - parseFloat(a.changePercent || ''));

    return (
      <>
        {dataCards.map(item => (
          <CalculatedTradePriceCache
            key={item.symbol}
            symbol={item.symbol}
            handleTradePriceCache={handleTradePriceCache}
          />
        ))}
        {sortedData.length > 0 && peopleAlsoTradeCards(sortedData.slice(slideIndex, slideIndex + 5))}
      </>
    );
  };

  const handleSlideIndex = (index: number) => {
    setSlideIndex(index);
  };

  useEffect(() => {
    if (accountReferenceId) {
      getDataPeopleAlsoTrade();
    }
  }, [accountReferenceId, symbol, getDataPeopleAlsoTrade]);

  useEffect(() => {
    if (peopleAlsoTradeData.length > 0) {
      setDataCards(peopleAlsoTradeData);
    }
  }, [peopleAlsoTradeData]);

  const companyLogoAndName = useSelector((state: RootState) => state.fundamental.companyLogoAndName);

  const subscriptionSymbols = useMemo(() =>
    peopleAlsoTradeData.map(item => ({
      symbol: item.symbol,
      stockExchangeCode: companyLogoAndName?.[item.symbol]?.stockExchangeCode,
      countryDesignation: companyLogoAndName?.[item.symbol]?.countryDesignation,
    })),
  [peopleAlsoTradeData, companyLogoAndName]);

  useSubscriptionManager({
    symbols: subscriptionSymbols,
    enabled: peopleAlsoTradeData.length > 0,
  });

  return (
    <div className={styles.wrapper}>
      <div className={isMobile ? styles.titleMobile : styles.title}>
        {lang.commonStockDetailsStatsPeopleAlsoTradeTitle()}
      </div>
      <div className={styles.fakeContainer}>
        <div className={styles.container}>
          {isPeopleAlsoTradeDataPending && (
            <div className={styles.loader}>
              <Preloader isFullScreen text="" owner="People also trade" />
            </div>
          )}
          {
            (isPeopleAlsoTradeDataReady && peopleAlsoTradeData.length === 0)
            && <div className={styles.noDataAvailable}><NoDataAvailable /></div>
          }
          {(isPeopleAlsoTradeDataReady && peopleAlsoTradeData.length > 0) && renderPeopleAlsoTradeCards()}
          { !isMobile && (
            <SlideButtons
              handleSlideIndex={handleSlideIndex}
              slideIndex={slideIndex}
              peopleAlsoTradeData={sortedData}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  peopleAlsoTradeData: state.reporting.peopleAlsoTradeData,
  accountReferenceId: getCurrentAccount(state),
  isPeopleAlsoTradeDataPending: isCallStatusPending(ReportingCall.getPeopleAlsoTradeData, state.reporting.statusByCall),
  isPeopleAlsoTradeDataReady: isCallStatusReady(ReportingCall.getPeopleAlsoTradeData, state.reporting.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getDataPeopleAlsoTrade: () => dispatch(getPeopleAlsoTradeData()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(PeopleAlsoTrade);
